import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import { AbbCardComponent } from '@components/abb-card/abb-card.component';
import { URLS } from '@constants/urls';
import { getStepperStatusQuery } from '@graphql/commissioning';
import { TranslateService } from '@ngx-translate/core';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { CognitoMfaPreferenceService } from '@services/cognito/cognito-mfa-preference.service';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { ToastService } from '@services/ui/toast.service';
import { getErrorMessage } from '@utils/errors';
import { QRCodeModule } from 'angularx-qrcode';
import { Apollo } from 'apollo-angular';
import { AbbCommonModule } from 'app/abb-common.module';
import {
  GetFirstAccessWizardStepperStatusQuery,
  GetFirstAccessWizardStepperStatusQueryVariables,
  GetLicenseIdQuery,
  GetLicenseIdQueryVariables,
  RegisterLoginMutation,
  UpdateUserAvatarMutation,
  UpdateUserAvatarMutationVariables,
  UtcQuery,
} from 'gql/graphql';
import { Subscription } from 'rxjs';
import { FIRST_ACCESS_WIZARD_LAST_STEP } from './wizard-first-access/wizard-first-access.component';
import { getLicenseIdQuery, updateUserAvatarMutation } from '@graphql/account';
import Validation from '@utils/validation';
import { PermissionsService } from '@services/user/permissions.service';
import { UserSettingsService } from '@services/user/user-settings.service';
import { utcQuery } from '@graphql/general';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { t } from '@utils/strings';
import { noop } from '@utils/runtime';
import { SignUpComponent } from '@pages/signup/signup.component';
import { registerLogin } from '@graphql/user';

@Component({
  selector: 'abb-login',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    MatCardModule,
    TPipe,
    AbbCommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatInputModule,
    AbbCardComponent,
    QRCodeModule,
    OverflowTooltipDirective,
  ],
  template: `
    <div
      class="relative flex items-center justify-center min-h-screen w-full p-4"
    >
      <!-- Video background -->
      <video
        #backgroundVideo
        class="absolute top-0 left-0 w-full h-full object-cover -z-10"
        autoplay
        muted
        loop
        playsinline
        preload="auto"
        (loadeddata)="onVideoLoaded()"
        (canplay)="onVideoCanPlay()"
      >
        <source
          src="https://new.abb.com/videos/librariesprovider84/smart-buildings/insite-energy-pro-loop2.mp4?sfvrsn=99f7670d_4"
          type="video/mp4"
        />
        <!-- Fallback per browser che non supportano il video -->
        <div
          class="absolute top-0 left-0 w-full h-full bg-center bg-cover bg-no-repeat"
          style="background-image: url(/assets/images/abb-bg.png);"
        ></div>
      </video>
      @if(!forgotPsw()) {
      <abb-card
        [cardTitle]="'Log in' | t"
        [cardHeight]="showConfirm ? 'h-auto' : 'h-[400px]'"
      >
        <ng-container card-content>
          @if(!showConfirm) {
          <form
            id="login-form"
            [formGroup]="form"
            (ngSubmit)="login()"
            class="h-full w-full flex flex-col flex-1 gap-4 justify-center"
          >
            <mat-form-field
              [hideRequiredMarker]="true"
              class="w-full bg-white-field"
              color="primary"
            >
              <mat-label>{{ 'Email address' | t }}</mat-label>
              <input matInput autofocus formControlName="email" name="email" />
              @if(form.get('email')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              } @if(form.get('email')?.hasError('email') ||
              form.get('email')?.hasError('pattern')){
              <mat-error>{{
                'Email is not valid (e.g., user@example.com)' | t
              }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field
              [hideRequiredMarker]="true"
              class="w-full bg-white-field"
              color="primary"
            >
              <mat-label>{{ 'Password' | t }}</mat-label>
              <input
                matInput
                formControlName="password"
                name="password"
                [type]="hidePsw() ? 'password' : 'text'"
              />
              <abb-icon
                matSuffix
                (click)="hidePsw.set(!hidePsw())"
                class="cursor-pointer pr-4 pt-4"
                [name]="hidePsw() ? 'mat/visibility_off' : 'mat/visibility'"
              />
              @if(form.get('password')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              } @if(form.get('password')?.hasError('minlength')){
              <mat-error>{{
                'Password must be at least 8 characters long' | t
              }}</mat-error>
              } @if(form.get('password')?.hasError('maxlength')){
              <mat-error>{{
                'Password must not exceed 256 characters' | t
              }}</mat-error>
              }
              <mat-hint align="end">
                <a
                  [ngClass]="{ 'pointer-events-none opacity-80': loading() === 'signingIn' }"
                  (click)="forgotPsw.set(true)"
                  class="text-xs text-abb-font/65 cursor-pointer"
                >
                  {{ 'Forgot password' | t }}?
                </a>
              </mat-hint>
            </mat-form-field>
          </form>
          } @else { @if(setupUri){
          <div class="flex flex-col">
            <p class="mb-4">
              {{
                'Scan this QR code with your authenticator app to configure Two-Factor Authentication'
                  | t
              }}:
            </p>
            <qrcode
              [qrdata]="setupUri"
              [errorCorrectionLevel]="'M'"
              [width]="250"
              [cssClass]="'flex flex-1 justify-center'"
            ></qrcode>
          </div>
          } @else {
            <p class="mb-4">
              @if (otpSource === 'email') {
                {{
                  'Please open your inbox and enter the verification code you received to continue'
                    | t
                }}.
              } @else {
                {{
                  'Please open your authenticator app and enter the verification code to continue'
                    | t
                }}.
              }
            </p>
          }
          <form
            id="confirm-signin-form"
            [formGroup]="confirmForm"
            (ngSubmit)="confirmSignIn()"
          >
            <mat-form-field class="w-full bg-white-field" color="primary">
              <mat-label>{{ 'Code' | t }}</mat-label>
              <input
                #codeInput
                matInput
                formControlName="code"
                name="code"
              />@if(confirmForm.get('code')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              } @if(confirmForm.get('code')?.hasError('invalid')){
              <mat-error>{{ 'Code is not valid' | t }}</mat-error>
              } @if(confirmForm.get('code')?.hasError('expired')){
              <mat-error>{{ 'Code has expired' | t }}</mat-error>
              }
            </mat-form-field>
          </form>
          }
        </ng-container>
        <ng-container card-actions>
          @if(!showConfirm){
          <div class="w-full flex flex-row justify-between items-center">
            <a
              [ngClass]="{ 'pointer-events-none opacity-80': loading() === 'signingIn' }"
              [routerLink]="'/sign-up'"
              class="text-abb-primary text-xs hover:underline"
              >{{ 'Don’t have an account? Sign-up' | t }}</a
            >
            <abb-button
              [type]="'submit'"
              form="login-form"
              [loading]="loading() === 'signingIn'"
              [disabled]="loading() === 'signingIn'"
              class="w-44"
              className="py-2.5 text-sm font-medium"
              >{{ 'Login' | t }}</abb-button
            >
          </div>
          } @else {
          <div class="w-full flex flex-row justify-end items-center">
            @if(!setupUri && !isFirstLogin()) {
              <div class="flex justify-center mr-auto">
                <a
                  [ngClass]="{ 'pointer-events-none opacity-80': loading()}"
                  (click)="sendOTPViaEmail($event)"
                  class="text-abb-primary text-xs hover:underline cursor-pointer"
                >
                  {{ 'Reset mfa' | t }}
                </a>
              </div>
            }
            <abb-button
              [type]="'submit'"
              form="confirm-signin-form"
              [disabled]="!confirmForm.valid || loading() === 'confirmingSignIn'"
              [loading]="loading() === 'confirmingSignIn'"
              class="w-44"
              className="py-2.5 text-sm font-medium"
            >
              {{ 'Confirm' | t }}</abb-button
            >
          </div>
        }
        </ng-container>
      </abb-card>
      } @else {
      <abb-card [cardTitle]="'Reset Password' | t" [cardHeight]="'h-auto'">
        <ng-container card-content>
          @if(forgotPswConfirmation()){
          <p class="mb-4">
            {{
              'Confirmation code was sent by {deliveryMedium} to {deliveryDestination}'
                | t
                  : {
                      deliveryMedium: deliveryMedium,
                      deliveryDestination: deliveryDestination
                    }
            }}
          </p>
          }
          <form
            id="reset-psw-form"
            [formGroup]="forgotPswForm"
            class="h-full w-full flex flex-col flex-1 gap-4 justify-center mt-4"
            (ngSubmit)="resetPassword()"
          >
            <mat-form-field class="w-full bg-white-field" color="primary">
              <mat-label>{{ 'Email address' | t }}</mat-label>
              <input
                matInput
                type="text"
                formControlName="email"
                name="email"
              />
              @if(forgotPswForm.get('email')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              } @if(forgotPswForm.get('email')?.hasError('email') ||
              forgotPswForm.get('email')?.hasError('pattern') ){
              <mat-error>{{
                'Email is not valid (e.g., user@example.com)' | t
              }}</mat-error>
              }
            </mat-form-field>
            @if(forgotPswConfirmation()){
            <mat-form-field class="w-full bg-white-field" color="primary">
              <mat-label>{{ 'Confirmation Code' | t }}</mat-label>
              <input
                matInput
                type="text"
                formControlName="confirmationCode"
                name="confirmationCode"
              />
              @if(forgotPswForm.get('confirmationCode')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              } @if(forgotPswForm.get('confirmationCode')?.hasError('invalid')){
              <mat-error>{{ 'Confirmation code is not valid' | t }}</mat-error>
              } @if(forgotPswForm.get('confirmationCode')?.hasError('expired')){
              <mat-error>{{ 'Confirmation code has expired' | t }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field class="w-full bg-white-field" color="primary">
              <mat-label>{{ 'New Password' | t }}</mat-label>
              <input
                matInput
                [type]="hidePsw() ? 'password' : 'text'"
                formControlName="newPassword"
                name="newPassword"
              />
              <abb-icon
                matSuffix
                (click)="hidePsw.set(!hidePsw())"
                class="cursor-pointer pr-4"
                [name]="hidePsw() ? 'mat/visibility_off' : 'mat/visibility'"
              />
              @if(forgotPswForm.get('newPassword')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              } @if(forgotPswForm.get('newPassword')?.hasError('minlength')){
              <mat-error>{{
                'Password must be at least 8 characters long' | t
              }}</mat-error>
              <!-- prettier-ignore -->
              } @else if(forgotPswForm.get('newPassword')?.hasError('maxlength')){
              <mat-error>{{
                'Password must not exceed 256 characters' | t
              }}</mat-error>
              <!-- prettier-ignore -->
              } @else if(forgotPswForm.get('newPassword')?.hasError('password')){
              <mat-error class="truncate" overflowTooltip>{{
                'Password must include uppercase and lowercase letters, a number, and a special character (e.g., !@#$%^&*())'
                  | t
              }}</mat-error>
              }
            </mat-form-field>
            }
          </form>
        </ng-container>
        <ng-container card-actions>
          @if(!forgotPswConfirmation()){
          <div class="w-full flex flex-row justify-end items-center gap-12">
            <abb-button
              (click)="forgotPsw.set(false)"
              [disabled]="loading() === 'resettingPassword'"
              variant="clean"
              >{{ 'Back' | t }}</abb-button
            >
            <abb-button
              [type]="'submit'"
              form="reset-psw-form"
              [disabled]="loading() === 'resettingPassword'"
              [loading]="loading() === 'resettingPassword'"
              class="w-44"
              className="py-2.5 text-sm font-medium"
              >{{ 'Reset Password' | t }}</abb-button
            >
          </div>
          } @else {
          <div class="w-full flex flex-row justify-end items-center">
            <abb-button
              [type]="'submit'"
              form="reset-psw-form"
              [loading]="loading() === 'confirmingResetPassword'"
              [disabled]="loading() === 'confirmingResetPassword'"
              class="w-44"
              className="py-2.5 text-sm font-medium"
            >
              {{ 'Confirm' | t }}</abb-button
            >
          </div>
          }
        </ng-container>
      </abb-card>
      }
    </div>
  `,
  styles: `
    .bg-white-field {
      --mdc-filled-text-field-container-color: var(--abb-color-white);
      --mat-form-field-state-layer-color: transparent;
    }
  `,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'login',
    },
  ],
  host: { class: 'block w-full h-full flex-1' },
})
export class LoginComponent implements OnDestroy, AfterViewInit {
  @ViewChild('backgroundVideo', { static: false })
  backgroundVideo!: ElementRef<HTMLVideoElement>;

  @ViewChild('codeInput', { static: false })
  codeInput!: ElementRef<HTMLInputElement>;

  prefix = inject(TRANSLATION_PREFIX);

  hidePsw = signal(true);
  forgotPsw = signal(false);
  forgotPswConfirmation = signal(false);
  deliveryMedium: string | undefined = undefined;
  deliveryDestination: string | undefined = undefined;
  forgotPswForm = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.email,
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ]),
    confirmationCode: new FormControl({ value: '', disabled: true }, []),
    newPassword: new FormControl({ value: '', disabled: true }, [
      Validators.minLength(8),
      Validators.maxLength(256),
      Validation.field('password'),
    ]),
  });

  form = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.email,
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(256),
    ]),
  });

  confirmForm = new FormGroup({
    code: new FormControl('', [Validators.required]),
  });

  loading = signal<
    | 'signingIn'
    | 'sendingEmailOTP'
    | 'confirmingSignIn'
    | 'resettingPassword'
    | 'confirmingResetPassword'
    | 'requestingOTPSetupQRCode'
    | null
  >(null);
  showConfirm: boolean = false;
  setupUri: string | undefined;
  user: Signal<UserWithID | null>;
  otpSource: 'email' | 'authenticator' | 'authenticator-verifying' =
    'authenticator';

  private subscriptions: Subscription[] = [];
  setupTOTPUsingEmail: boolean;

  constructor(
    private readonly toast: ToastService,
    private readonly cognitoMfaPreferenceService: CognitoMfaPreferenceService,
    private readonly cognitoService: CognitoService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly apollo: Apollo,
    private readonly permissionsService: PermissionsService,
    private readonly userService: UserSettingsService
  ) {
    this.user = this.cognitoService.loggedUser;
    this.setupTOTPUsingEmail =
      this.router.routerState.snapshot.url.includes('setupTOTP=1');
  }

  ngAfterViewInit(): void {
    // Forza l'avvio del video dopo che la view è stata inizializzata
    this.ensureVideoPlays();
  }

  onVideoLoaded(): void {
    // Il video è stato caricato, prova a farlo partire
    this.ensureVideoPlays();
  }

  onVideoCanPlay(): void {
    // Il video può essere riprodotto, prova a farlo partire
    this.ensureVideoPlays();
  }

  private ensureVideoPlays(): void {
    if (this.backgroundVideo?.nativeElement) {
      const video = this.backgroundVideo.nativeElement;
      video.muted = true; // Assicurati che sia muto per l'autoplay

      // Prova a far partire il video
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video started successfully
            console.log('Background video started successfully');
          })
          .catch((error) => {
            console.warn('Background video autoplay was prevented:', error);
            // Fallback: prova a far partire il video su user interaction
            this.setupVideoInteractionFallback();
          });
      }
    }
  }

  private setupVideoInteractionFallback(): void {
    if (this.backgroundVideo?.nativeElement) {
      const video = this.backgroundVideo.nativeElement;

      // Handler per far partire il video al primo click/touch sulla pagina
      const startVideoOnInteraction = () => {
        video
          .play()
          .then(() => {
            console.log('Background video started after user interaction');
            // Rimuovi gli event listeners una volta che il video è partito
            document.removeEventListener('click', startVideoOnInteraction);
            document.removeEventListener('touchstart', startVideoOnInteraction);
          })
          .catch((error) => {
            console.warn(
              'Could not start background video even after user interaction:',
              error
            );
          });
      };

      // Aggiungi gli event listeners
      document.addEventListener('click', startVideoOnInteraction, {
        once: true,
      });
      document.addEventListener('touchstart', startVideoOnInteraction, {
        once: true,
      });
    }
  }

  async login() {
    if (this.form.invalid) {
      return;
    }
    this.loading.set('signingIn');
    try {
      if (this.setupTOTPUsingEmail) {
        await this.cognitoMfaPreferenceService.toggleMFAPreference(
          {
            email: this.form.value.email!,
            password: this.form.value.password!,
          },
          'email'
        );
        this.setupTOTPUsingEmail = false;
        this.otpSource = 'email';
      } else {
        this.otpSource = 'authenticator';
      }

      const signInResult = await this.cognitoService.signIn({
        email: this.form.value.email!,
        password: this.form.value.password!,
      });
      if (signInResult) {
        // TODO Refactor in an explaining method
        this.setupUri = signInResult.href ?? String(signInResult);
        this.otpSource = 'authenticator-verifying';
      }

      this.showConfirm = true;
      if (this.isFirstLogin()) {
        this.otpSource = 'authenticator-verifying';
        return;
      }

      if (this.otpSource === 'email') {
        this.toast.notifySuccess(
          this.translate.instant('login.OTP sent, please check your inbox')
        );
      }

      // TODO translate  comment
      // Focus sul campo codice dopo che la view è stata aggiornata
      setTimeout(() => {
        this.focusCodeInput();
      }, 100);
    } catch (err) {
      console.error('SignIn error:', err);
      let errMsg = getErrorMessage(err, this.translate);
      this.toast.notifyError(errMsg);
    } finally {
      this.loading.set(null);
    }
  }

  sendOTPViaEmail(e: Event) {
    e.preventDefault();
    if (this.loading()) {
      return;
    }
    this.loading.set('sendingEmailOTP');
    this.cognitoService
      .signOut()
      .catch(noop)
      .then(async () => {
        await this.cognitoMfaPreferenceService.toggleMFAPreference(
          {
            email: this.form.value.email!,
            password: this.form.value.password!,
          },
          'email'
        );
        await this.cognitoService.signIn({
          email: this.form.value.email!,
          password: this.form.value.password!,
        });
      })
      .then(() => {
        this.showConfirm = true;
        this.otpSource = 'email';

        this.toast.notifySuccess(
          this.translate.instant('login.OTP sent, please check your inbox')
        );

        // Focus sul campo codice dopo che la view è stata aggiornata
        setTimeout(() => {
          this.focusCodeInput();
        }, 100);
      })
      .catch((err) => {
        console.error('SignIn error:', err);
        let errMsg = getErrorMessage(err, this.translate);
        this.toast.notifyError(errMsg);
      })
      .finally(() => this.loading.set(null));
  }

  confirmSignIn(): void {
    // TODO test if the following line is enough
    if (this.confirmForm.invalid) {
    // if (!this.cognitoService.isFirstLoginValue && this.confirmForm.invalid) {
      return;
    }
    this.loading.set('confirmingSignIn');
    (this.otpSource === 'authenticator-verifying'
      ? this.cognitoService.verifyTOTPSetup(this.confirmForm.value.code!)
      : this.cognitoService.confirmSignIn(this.confirmForm.value.code!)
    )
      .then(async () => {
        if (this.otpSource === 'email') {
          await this.cognitoMfaPreferenceService.disableTOTP({
            email: this.form.value.email!,
            password: this.form.value.password!,
          });
          this.setupUri = (await this.cognitoService.setUpTOTP()).href;
          this.otpSource = 'authenticator-verifying';
          this.loading.set(null);
          this.confirmForm.controls.code.setValue('');
          return;
        }

        await this.rerouteToApplication();
      })
      .catch((err) => {
        this.loading.set(null);
        console.error('SignIn confirmation error:', err);
        if (err.name === 'CodeMismatchException') {
          this.confirmForm.get('code')?.setErrors({ invalid: true });
        } else if (err.name === 'ExpiredCodeException') {
          this.confirmForm.get('code')?.setErrors({ expired: true });
        }
        let errMsg = getErrorMessage(err, this.translate);
        this.toast.notifyError(errMsg);
      })
      .finally(() => {
        const lang = this.cognitoService.language();
        if (!!lang) {
          this.translate.use(lang).subscribe({
            next: () => {
              console.log(`Language set to: ${lang}`);
            },
            error: (err) => {
              console.error('Error initializing translations: ', err);
            },
          });
        }
      });
  }

  private async rerouteToApplication() {
    await this.checkLicense();
    this.userService.setUserAccount(null);
    await this.userService.getUserAccount();
    await this.userService.getUserAvatar();
    const role = await this.permissionsService.getUserRole();
    if (role) {
      await this.permissionsService.getPermissions(role);
    }
    await this.getUserTimezone();
    await this.userService.getUserUISettings();
    if (SignUpComponent.pendingImgUrl) {
      const [email, imgUrl] = SignUpComponent.pendingImgUrl;
      SignUpComponent.pendingImgUrl = null;
      if (email === this.user()?.email) {
        this.apollo
          .mutate<
            UpdateUserAvatarMutation,
            UpdateUserAvatarMutationVariables
          >({
            mutation: updateUserAvatarMutation,
            variables: {
              id: this.user()?.id ?? '',
              imgUrl: imgUrl ?? '',
            },
          })
          .subscribe({
            next: ({ data }) => {
              this.cognitoService.updateLoggedInUserImgUrl(
                data?.updateUserAvatar ?? ''
              );
            },
          });
      }
    }
    this.subscriptions.push(
      this.apollo
        .watchQuery<
          GetFirstAccessWizardStepperStatusQuery,
          GetFirstAccessWizardStepperStatusQueryVariables
        >({
          query: getStepperStatusQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.cognitoService.setUserFirstAccessStep(
              data.getFirstAccessWizardStepperStatus
            );
            const step = data.getFirstAccessWizardStepperStatus.step;
            const nextRoute = step != FIRST_ACCESS_WIZARD_LAST_STEP
              ? [URLS.BASE, URLS.FIRST_ACCESS_WIZARD]
              : [URLS.BASE];
            this.router.navigate(nextRoute).finally(() => {
              this.loading.set(null);
            });
          },
          error: (err) => {
            this.router.navigate([URLS.BASE]).finally(() => {
              this.loading.set(null);
            });
          },
        })
    );
  }

  private getUserTimezone(): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log(this.userService.timezone());
      if (this.userService.timezone() && this.userService.timezone() != '') {
        resolve();
      } else {
        this.subscriptions.push(
          this.apollo
            .watchQuery<UtcQuery>({
              query: utcQuery,
            })
            .valueChanges.subscribe({
              next: (res) => {
                this.userService.setUserTimezone(
                  res.data.regionSettings.timezone.utc_offset ?? ''
                );
                resolve();
              },
              error: (err) => {
                console.error(err);
                //let errMsg = getErrorMessage(err, this.translate);
                //this.toast.notifyError(errMsg);
                resolve();
              },
            })
        );
      }
    });
  }

  private checkLicense(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.apollo
          .watchQuery<GetLicenseIdQuery, GetLicenseIdQueryVariables>({
            query: getLicenseIdQuery,
          })
          .valueChanges.subscribe({
            next: (res) => {
              this.cognitoService.updateLoggedInUserLicenseId(
                res.data.getLicenseId
              );
              resolve();
            },
            error: (err) => {
              console.error(err);
              //let errMsg = getErrorMessage(err, this.translate);
              //this.toast.notifyError(errMsg);
              resolve();
            },
          })
      );
    });
  }

  resetPassword(): void {
    if (!this.forgotPswConfirmation()) {
      if (!this.forgotPswForm.valid) {
        return;
      }
      this.loading.set('resettingPassword');
      this.cognitoService
        .resetPassword(this.forgotPswForm.value.email!)
        .then((res) => {
          if (res) {
            this.deliveryMedium = res?.delivery?.toLowerCase();
            this.deliveryDestination = res?.destination;
            this.forgotPswForm.controls.confirmationCode.enable();
            this.forgotPswForm.controls.newPassword.enable();
            this.forgotPswConfirmation.set(true);
          }
        })
        .catch((err) => {
          console.error('Reset password error:', err);
          let errMsg = getErrorMessage(err, this.translate);
          this.toast.notifyError(errMsg);
        })
        .finally(() => this.loading.set(null));
    } else {
      this.forgotPswForm.controls.confirmationCode.addValidators([
        Validators.required,
      ]);
      this.forgotPswForm.controls.newPassword.addValidators([
        Validators.required,
      ]);
      this.forgotPswForm.controls.confirmationCode.updateValueAndValidity();
      this.forgotPswForm.controls.newPassword.updateValueAndValidity();
      this.confirmResetPassword();
    }
  }

  confirmResetPassword() {
    if (!this.forgotPswForm.valid) {
      return;
    }
    this.loading.set('confirmingResetPassword');
    this.cognitoService
      .confirmResetPassword({
        username: this.forgotPswForm.value.email!,
        confirmationCode: this.forgotPswForm.value.confirmationCode!,
        newPassword: this.forgotPswForm.value.newPassword!,
      })
      .then(() => {
        this.toast.notifySuccess(
          t(
            this.prefix,
            `Password has been successfully reset: you can now log in with your new password`,
            this.translate
          )
        );
        this.forgotPsw.set(false);
        this.forgotPswConfirmation.set(false);
      })
      .catch((err) => {
        console.error('Confirm password reset error:', err);
        if (err.name === 'CodeMismatchException') {
          this.forgotPswForm
            .get('confirmationCode')
            ?.setErrors({ invalid: true });
        } else if (err.name === 'ExpiredCodeException') {
          this.forgotPswForm
            .get('confirmationCode')
            ?.setErrors({ expired: true });
        }
        let errMsg = getErrorMessage(err, this.translate);
        this.toast.notifyError(errMsg);
      })
      .finally(() => this.loading.set(null));
  }

  private focusCodeInput(): void {
    if (this.codeInput?.nativeElement) {
      try {
        this.codeInput.nativeElement.focus();
      } catch (error) {
        console.warn('Could not focus code input:', error);
      }
    } else {
      setTimeout(() => {
        if (this.codeInput?.nativeElement) {
          this.codeInput.nativeElement.focus();
        }
      }, 50);
    }
  }

  isFirstLogin(): boolean {
    return this.cognitoService.isFirstLoginValue();
  }


  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
